import { useEffect, useState } from 'preact/hooks';
import { api } from '../api/client';
import type { PublicCollectionSummary, PublicProfileDto } from '../api/types';
import { Layout } from '../components/Layout';
import { Eyebrow, PageContainer } from '../primitives';
import { useTranslation } from '../i18n';

/**
 * Public profile page at <c>/u/:slug</c>. Per ADR-0015 + legacy
 * ADR-0030 the navbar avatar/name links here for the signed-in user
 * (and any visitor opening another user's slug). The shape mirrors the
 * legacy account-rail's "My profile" view: header (avatar + display
 * name + slug + bio + social chips) + a Public Collections grid below.
 *
 * <p>Visibility is enforced server-side by
 * <c>PublicProfileController</c>: Private profiles return 404 to non-
 * owners; Unlisted profiles return 200 but are not listed on member
 * search. Owners always reach their own profile; the SPA renders an
 * "Edit profile" affordance pointing at the (deferred) settings hub.</p>
 *
 * <p>Collections list comes from
 * <c>GET /api/u/{slug}/collections</c> per Phase 1.2; only
 * <c>Public</c> collections show to non-owners (Unlisted is hidden
 * from the list but reachable via direct link).</p>
 */
export function ProfilePage({ slug }: { slug: string }) {
    const t = useTranslation();
    const [profile, setProfile] = useState<PublicProfileDto | null | 'not-found'>(null);
    const [collections, setCollections] = useState<PublicCollectionSummary[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const p = await api.users.getProfile(slug);
                if (cancelled) return;
                setProfile(p);
                // Collections endpoint may legitimately fail (deleted
                // owner, transient); render the profile regardless.
                try {
                    const c = await api.users.listPublicCollections(slug);
                    if (!cancelled) setCollections(c);
                } catch {
                    // Swallow: profile renders, collections panel just stays empty.
                }
            } catch (e) {
                if (cancelled) return;
                if (e instanceof Error && e.message.includes('404')) {
                    setProfile('not-found');
                } else {
                    setError(e instanceof Error ? e.message : String(e));
                }
            }
        }
        void load();
        return () => {
            cancelled = true;
        };
    }, [slug]);

    if (profile === null) {
        return (
            <Layout>
                <PageContainer>
                    <p class="py-12 text-center text-sm text-fg-muted">{t('profile.loading')}</p>
                </PageContainer>
            </Layout>
        );
    }

    if (profile === 'not-found') {
        return (
            <Layout>
                <PageContainer>
                    <div class="py-16 text-center">
                        <Eyebrow>{t('profile.notFound.eyebrow')}</Eyebrow>
                        <h1 class="mt-2 font-display text-3xl">{t('profile.notFound.title')}</h1>
                        <p class="mt-4 text-sm text-fg-muted">
                            {t('profile.notFound.body', { slug })}
                        </p>
                        <a
                            href="/emporium"
                            class="mt-6 inline-flex rounded border border-rule-strong px-4 py-2 text-sm hover:border-accent"
                        >
                            {t('profile.notFound.back')}
                        </a>
                    </div>
                </PageContainer>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <PageContainer>
                    <div class="py-12 text-center text-sm text-state-failed">{error}</div>
                </PageContainer>
            </Layout>
        );
    }

    return (
        <Layout>
            <PageContainer>
                <header class="flex flex-col items-start gap-5 border-b border-rule pb-8 sm:flex-row sm:items-center sm:gap-7">
                    <div
                        class="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-full bg-accent text-3xl text-accent-ink"
                        aria-hidden="true"
                    >
                        {profile.avatar ? (
                            <img
                                src={profile.avatar}
                                alt=""
                                class="h-full w-full object-cover"
                            />
                        ) : (
                            <span class="font-display italic">
                                {(profile.displayName?.[0] ?? profile.slug[0] ?? '?').toUpperCase()}
                            </span>
                        )}
                    </div>
                    <div class="min-w-0 flex-1">
                        <Eyebrow>{t('profile.memberEyebrow')}</Eyebrow>
                        <h1 class="mt-1 font-display text-[clamp(28px,4vw,42px)] leading-tight">
                            {profile.displayName ?? profile.slug}
                        </h1>
                        <p class="mt-1 font-mono text-[12px] text-fg-muted">
                            @{profile.slug}
                            {profile.visibility !== 'Public' && (
                                <span class="ml-3 rounded border border-rule px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] text-fg-faint">
                                    {profile.visibility}
                                </span>
                            )}
                        </p>
                        {profile.bio && (
                            <p class="mt-3 max-w-[60ch] text-[14.5px] text-fg-secondary">
                                {profile.bio}
                            </p>
                        )}
                        {profile.socialLinks.length > 0 && (
                            <div class="mt-3 flex flex-wrap gap-2">
                                {profile.socialLinks.map((l) => (
                                    <a
                                        key={l.url}
                                        href={l.url}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        class="inline-flex items-center gap-1.5 rounded-full border border-rule-strong px-2.5 py-1 text-[12px] text-fg-muted hover:border-accent hover:text-accent"
                                    >
                                        <span class="font-mono text-[10px] uppercase tracking-[0.12em]">
                                            {l.kind}
                                        </span>
                                        <span class="truncate">{l.handle}</span>
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                    {profile.isOwner && (
                        <a
                            href="/me/account"
                            class="rounded border border-rule-strong px-4 py-2 text-sm hover:border-accent"
                        >
                            {t('profile.editProfile')}
                        </a>
                    )}
                </header>

                <section class="mt-10">
                    <Eyebrow class="mb-4">
                        {t('profile.publicCollections.eyebrow')}{' '}
                        <span class="text-fg-faint">({collections.length})</span>
                    </Eyebrow>
                    {collections.length === 0 ? (
                        <p class="text-sm text-fg-muted">
                            {profile.isOwner
                                ? t('profile.publicCollections.emptyOwner')
                                : t('profile.publicCollections.emptyVisitor')}
                        </p>
                    ) : (
                        <ul class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {collections.map((c) => (
                                <li
                                    key={c.id}
                                    class="rounded-md border border-rule bg-surface-raised p-4 transition-colors hover:border-accent"
                                >
                                    <a
                                        href={`/u/${encodeURIComponent(profile.slug)}/c/${encodeURIComponent(c.slug)}`}
                                        class="block"
                                    >
                                        <div class="font-medium text-fg-primary">{c.name}</div>
                                        {c.description && (
                                            <p class="mt-1 line-clamp-2 text-[13px] text-fg-muted">
                                                {c.description}
                                            </p>
                                        )}
                                        <div class="mt-2 font-mono text-[11px] uppercase tracking-[0.14em] text-fg-faint">
                                            {c.memberCount} {c.memberCount === 1 ? t('profile.publicCollections.work') : t('profile.publicCollections.works')}
                                        </div>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </PageContainer>
        </Layout>
    );
}
